import React, { useState } from 'react';
import FormError from '../../../../FormError';
import { formatPhoneAsTyped } from '../../add/formatPhoneAsTyped';

let localKeySeq = 0;
function nextLocalKey() {
    localKeySeq += 1;
    return `contact-local-${localKeySeq}`;
}

export function emptyContactRow() {
    return {
        key: nextLocalKey(), id: null, first_name: '', last_name: '', phone: '', email: '', reminder_date: '', notes: '',
    };
}

function errorFor(errors, index, field) {
    return errors?.[`contacts.${index}.${field}`]?.[0] || null;
}

function matchesSearch(row, needle) {
    if (needle === '') {
        return true;
    }
    return [row.first_name, row.last_name, row.email, row.phone, row.notes]
        .join(' ')
        .toLowerCase()
        .includes(needle);
}

export default function ContactsBlock({
    contacts, onChange, errors, timezone = 'UTC',
}) {
    const [search, setSearch] = useState('');

    const updateField = (index, field, value) => {
        const next = contacts.slice();
        next[index] = { ...next[index], [field]: value };
        onChange(next);
    };

    const removeRow = (index) => {
        onChange(contacts.filter((row, i) => i !== index));
    };

    const addRow = () => {
        setSearch('');
        onChange([...contacts, emptyContactRow()]);
    };

    const needle = search.trim().toLowerCase();
    // Keep each row's index in the full list so edits/removes hit the right record
    // even while the table is filtered by the search box.
    const visibleRows = contacts
        .map((row, index) => ({ row, index }))
        .filter(({ row }) => matchesSearch(row, needle));

    const cellInput = (index, field, type, label, formatValue) => (
        <>
            <input
                id={`demo-edit-more-contacts-${index}-${field}`}
                type={type}
                aria-label={label}
                className="form-control form-control-sm"
                value={contacts[index][field]}
                onChange={(event) => updateField(index, field, formatValue ? formatValue(event.target.value) : event.target.value)}
            />
            <FormError error_message={errorFor(errors, index, field)} />
        </>
    );

    return (
        <div className="demo-edit-contacts">
            <div className="form-group">
                <input
                    type="search"
                    aria-label={__('Search contacts')}
                    className="form-control form-control-sm demo-edit-contacts-search"
                    placeholder={__('Search contacts...')}
                    value={search}
                    onChange={(event) => setSearch(event.target.value)}
                />
            </div>

            <div className="demo-edit-contacts-scroll">
                <table className="table table-sm demo-edit-contacts-table mb-0">
                    <thead>
                        <tr>
                            <th>{__('First Name')}</th>
                            <th>{__('Last Name')}</th>
                            <th>{__('Email')}</th>
                            <th>{__('Phone')}</th>
                            <th>{__('Reminder date')}<br /><small className="text-muted">{`(${timezone})`}</small></th>
                            <th>{__('Notes')}</th>
                            <th className="demo-edit-contacts-actions">{__('Actions')}</th>
                        </tr>
                    </thead>
                    <tbody>
                        {visibleRows.length === 0 &&
                            <tr>
                                <td colSpan={7} className="text-muted text-center">
                                    {contacts.length === 0 ? __('No contacts yet.') : __('No contacts match your search.')}
                                </td>
                            </tr>
                        }
                        {visibleRows.map(({ row, index }) => (
                            <tr key={row.key} data-testid={`demo-edit-more-contacts-row-${index}`}>
                                <td>{cellInput(index, 'first_name', 'text', __('First Name'))}</td>
                                <td>{cellInput(index, 'last_name', 'text', __('Last Name'))}</td>
                                <td>{cellInput(index, 'email', 'email', __('Email'))}</td>
                                <td>{cellInput(index, 'phone', 'text', __('Phone'), (value) => formatPhoneAsTyped(value, contacts[index].phone))}</td>
                                <td>{cellInput(index, 'reminder_date', 'datetime-local', __('Reminder date'))}</td>
                                <td>{cellInput(index, 'notes', 'text', __('Notes'))}</td>
                                <td className="demo-edit-contacts-actions text-center">
                                    <button
                                        type="button"
                                        className="btn btn-link text-danger p-0"
                                        aria-label={__('Remove')}
                                        title={__('Remove')}
                                        onClick={() => removeRow(index)}
                                    >
                                        <i className="fas fa-trash" aria-hidden="true" />
                                    </button>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            <button type="button" className="btn btn-secondary" style={{ marginTop: 10, marginBottom: 20 }} onClick={addRow}>
                {__('Add contact')}
            </button>
        </div>
    );
}
